import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Activity,
  Droplet,
  AlertTriangle,
  Sparkles,
  Loader2,
  CheckCircle2
} from 'lucide-react';

import { API_URL } from '../config';

export default function ReferentielsGastroEPage() {
  const [aiLoading, setAiLoading] = useState({});
  const [aiResults, setAiResults] = useState({});

  const handleAiAnalysis = async (protocolId, protocolName, content) => {
    setAiLoading({ ...aiLoading, [protocolId]: true });
    
    try {
      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          context: 'referentiels_gastro',
          audience: 'adult',
          prompt: `Analyse gastro-entérologique pour ${protocolName}:

${content.slice(0, 500)}

Fournis une analyse synthétique avec:
1. Évaluation de la gravité (score de Glasgow-Blatchford implicite)
2. Priorités de réanimation (remplissage, transfusion)
3. Traitement hémostatique (IPP, sandostatine)
4. Indications endoscopiques urgentes
5. Critères d'orientation`
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResults({ ...aiResults, [protocolId]: data.response });
        toast.success(`Analyse AI ${protocolName} complétée`);
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setAiLoading({ ...aiLoading, [protocolId]: false });
    }
  };

  const protocols = [
    {
      id: 'hemorragie-digestive',
      name: 'Hémorragie Digestive',
      icon: Droplet,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-400/30',
      content: `**HÉMORRAGIE DIGESTIVE**

**DIAGNOSTIC**

**Présentation clinique:**
• **Hématémèse** (vomissement de sang)
• **Melaena** (selles noires, goudronneuses)
  - Oriente plutôt vers un saignement haut
• **Rectorragie** (émission de sang rouge par l'anus)

**Orientation étiologique:**

**Hypertension portale suspectée:**
• Cirrhose connue et/ou clinique en faveur:
  - Alcoolisme
  - Ictère
  - Angiomes stellaires
  - Ascite

**Ulcère suspecté:**
• ATCD d'ulcère
• Traitement AINS, aspirine
• Clinique en faveur

**SIGNES DE GRAVITÉ**

• Malaise
• **Signes de choc hémorragique:**
  - Hypotension
  - Tachycardie
  - Marbrures
  - TRC allongé
• Traitements anticoagulants et antiagrégants plaquettaires

**MISE EN CONDITION**

• **2 VVP de bon calibre**
  - Cathéters 16-18 G minimum
• **Hémocue® répétés**
  - Surveillance hémoglobine capillaire
• **⚠ Aucun intérêt de la sonde naso-gastrique**
• **Bilan sanguin:**
  - Groupe et RAI
  - NFS
  - Hémostase
  - Fonction rénale

**TRAITEMENT**

**1. Antagonisation des anticoagulants:**
• À discuter selon:
  - Type d'anticoagulant
  - Gravité du saignement
  - Délai depuis dernière prise

**2. Si choc hémorragique:**
• Cf protocole choc hémorragique
• **Remplissage vasculaire:**
  - NaCl 0,9%
  - Cristalloïdes en première intention
• **Transfusion si nécessaire:**
  - Concentrés de globules rouges (CGR)
  - Objectif Hb > 7-8 g/dL

**3. Traitement spécifique:**

**Inhibiteurs de la pompe à protons (IPP):**
• **Pantoprazole (Eupantol®) 80 mg en IVD**
• Relais IVSE 8 mg/h après endoscopie si:
  - Ulcère FORREST I (hémorragie active)
  - Ulcère FORREST IIa (vaisseau visible)
  - Ulcère FORREST IIb (caillot adhérent)

**Si hypertension portale (HTP) suspectée:**
• Ajouter **Sandostatine:**
  - Bolus de 25 µg IV
  - Puis relais IVSE à 25 µg/h

**Si hématémèse cataclysmique chez patient cirrhotique:**
• **Sonde de Blakemore**
  - Tamponnement œsophagien
  - Mesure de dernier recours

**PROCÉDURES ENDOSCOPIQUES**

**En cas de saignement haut:**
• **EOGD (Endoscopie Œso-Gastro-Duodénale)**
• Dès que le patient est mis en conditions et stabilisé
• **Si signes de gravité → en URGENCE:**
  - Après contact gastro-entérologue d'astreinte
  - **Érythromycine 250 mg** 30 minutes avant l'examen
    (CI: QT long)
  - Améliore les conditions de visualisation

**En cas de rectorragie stable:**
• **Coloscopie dans les 24h**
• Avec préparation par PEG (Polyéthylène Glycol)

**En cas d'hémorragie digestive basse avec état de choc:**
• EOGD (éliminer cause haute?)
• **Angioscanner** pour discuter d'une embolisation

**Si rectorragie cataclysmique:**
• Et embolisation impossible
• Envisager **chirurgie d'hémostase**

**ORIENTATION**

**Journée:**
• Au cas par cas selon:
  - Gravité
  - Situation géographique
  - Disponibilité locale

**Soir et week-end:**
• **CH St Brieuc**
• Après validation régulation
• Après contact gastro-entérologue d'astreinte

**POINTS CLÉS**

✓ Mise en condition rapide (2 VVP gros calibre)
✓ IPP à dose de charge SYSTÉMATIQUE
✓ Sandostatine si cirrhose suspectée
✓ Pas de sonde naso-gastrique
✓ Endoscopie URGENTE si instabilité
✓ Antagonisation anticoagulants à discuter

**À SAVOIR**

• La classification de FORREST évalue le risque de récidive hémorragique:
  - **FORREST Ia:** Hémorragie en jet (risque élevé)
  - **FORREST Ib:** Hémorragie en nappe (risque élevé)
  - **FORREST IIa:** Vaisseau visible sans saignement (risque intermédiaire)
  - **FORREST IIb:** Caillot adhérent (risque intermédiaire)
  - **FORREST IIc:** Tache pigmentée (risque faible)
  - **FORREST III:** Ulcère à fond propre (risque très faible)

• L'hématémèse témoigne généralement d'un saignement en amont de l'angle de Treitz (saignement haut)

• Le melaena peut survenir avec un saignement de seulement 50-100 mL

• Un saignement digestif haut massif peut se présenter comme une rectorragie si transit accéléré`
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="referentiels-gastro-container">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-400/30">
            <Droplet className="w-8 h-8 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
              Référentiels Gastro-Entérologie
            </h1>
            <p className="text-[hsl(var(--muted-2))] mt-1">
              Protocoles urgences digestives - SMUR 2025
            </p>
          </div>
        </div>

        {/* Alert */}
        <Alert className="border-red-400/50 bg-red-500/10">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <AlertDescription className="text-red-300 font-medium">
            <strong>Hémorragie digestive:</strong> 2 VVP gros calibre • IPP 80mg IVD immédiat • Remplissage si choc • Endoscopie urgente si instabilité • Pas de sonde naso-gastrique
          </AlertDescription>
        </Alert>
      </div>

      {/* Protocols Card */}
      <Card className="border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Activity className="w-6 h-6 text-red-400" />
            Protocoles Gastro-Entérologiques
          </CardTitle>
          <CardDescription>
            Sélectionnez un protocole pour afficher les recommandations détaillées
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="space-y-4">
            {protocols.map((protocol) => {
              const Icon = protocol.icon;
              const isLoading = aiLoading[protocol.id];
              const aiResult = aiResults[protocol.id];

              return (
                <AccordionItem 
                  key={protocol.id} 
                  value={protocol.id}
                  className={`border ${protocol.border} ${protocol.bg} rounded-lg overflow-hidden`}
                  data-testid="protocol-item"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline">
                    <div className="flex items-center gap-3 text-left">
                      <div className={`p-2 rounded-lg ${protocol.bg} border ${protocol.border}`}>
                        <Icon className={`w-5 h-5 ${protocol.color}`} />
                      </div>
                      <span className="font-semibold text-lg">{protocol.name}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="px-6 pb-6 space-y-6">
                      <ScrollArea className="h-auto max-h-[600px]">
                        <div className="prose prose-invert max-w-none pr-4">
                          <div className="text-[hsl(var(--text))] space-y-4">
                            {protocol.content.split('\n').map((line, idx) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                const text = line.replace(/\*\*/g, '');
                                if (line.includes('DIAGNOSTIC') || line.includes('TRAITEMENT') || line.includes('MISE EN CONDITION')) {
                                  return <h3 key={idx} className="text-xl font-bold text-cyan-400 mt-6 mb-3">{text}</h3>;
                                }
                                return <h4 key={idx} className="text-lg font-semibold text-cyan-300 mt-4 mb-2">{text}</h4>;
                              }
                              if (line.trim().startsWith('•')) {
                                return <li key={idx} className="ml-4 text-[hsl(var(--text))] leading-relaxed">{line.replace('•', '').trim()}</li>;
                              }
                              if (line.trim().startsWith('-')) {
                                return <li key={idx} className="ml-8 text-[hsl(var(--muted-2))] text-sm leading-relaxed list-disc">{line.replace('-', '').trim()}</li>;
                              }
                              if (line.trim().startsWith('✓')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-cyan-500/10 rounded-lg border border-cyan-400/30 my-2">
                                    <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-cyan-300 font-medium">{line.replace('✓', '').trim()}</span>
                                  </div>
                                );
                              }
                              if (line.trim().startsWith('⚠')) {
                                return (
                                  <div key={idx} className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg border border-orange-400/30 my-2">
                                    <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-orange-300 font-medium">{line.replace('⚠', '').trim()}</span>
                                  </div>
                                );
                              }
                              if (line.trim()) {
                                return <p key={idx} className="text-[hsl(var(--text))] leading-relaxed">{line}</p>;
                              }
                              return <div key={idx} className="h-2" />;
                            })}
                          </div>
                        </div>
                      </ScrollArea>

                      <Button
                        onClick={() => handleAiAnalysis(protocol.id, protocol.name, protocol.content)}
                        disabled={isLoading}
                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
                        data-testid={`ai-button-${protocol.id}`}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Analyse AI en cours...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Analyse AI de ce protocole
                          </>
                        )}
                      </Button>

                      {aiResult && (
                        <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-lg">
                          <h3 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                            <Sparkles className="w-5 h-5" />
                            Analyse AI
                          </h3>
                          <div className="prose prose-invert max-w-none">
                            <div className="text-[hsl(var(--text))] whitespace-pre-wrap text-sm" data-testid={`ai-result-${protocol.id}`}>
                              {aiResult}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}
